import React, { useEffect, useState, useRef, useCallback } from 'react'
import type { User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase/browser'
import { logger } from '../lib/logger'
import { AuthContext, type AuthUser, type Profile } from './AuthContextCore'

const SESSION_TIMEOUT = 10 * 60 * 1000 // 10 minutes in milliseconds
const ACTIVITY_THROTTLE = 30 * 1000 // Only reset timeout every 30 seconds to avoid excessive resets
const LAST_ACTIVITY_KEY = 'hotel_mgmt_last_activity'

const isMissingAuthSessionError = (error: unknown) => {
  const authError = error as { name?: string; message?: string } | null
  return authError?.name === 'AuthSessionMissingError' ||
    /auth session missing/i.test(authError?.message || '')
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const currentUserIdRef = useRef<string | null>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const isLoadingRef = useRef(isLoading)

  useEffect(() => {
    isLoadingRef.current = isLoading
  }, [isLoading])

  // Function to reset session timeout
  const resetSessionTimeout = useCallback((isFromOtherTab = false) => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current)
    }
    
    if (user) {
      if (!isFromOtherTab) {
        logger.debug('Setting session timeout for 10 minutes')
        // Update local storage to notify other tabs
        try {
          localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString())
        } catch (e) {
          // Ignore storage errors
        }
      } else {
        logger.debug('Resetting session timeout due to activity in another tab')
      }

      sessionTimeoutRef.current = setTimeout(async () => {
        logger.warn('Session timeout - logging out user', { 
          userId: user.id, 
          lastActivity: new Date(lastActivityRef.current).toISOString(),
          now: new Date().toISOString()
        })
        try {
          const { error } = await supabase.auth.signOut()
          if (error && !isMissingAuthSessionError(error)) {
            logger.error('Error during timeout logout', error)
          }
        } catch (error) {
          if (!isMissingAuthSessionError(error)) {
            logger.error('Error during timeout logout', error)
          }
        }
      }, SESSION_TIMEOUT)
    }
  }, [user])

  // Function to check if user is still active (not just tab switching)
  const checkUserActivity = useCallback(() => {
    if (user) {
      const now = Date.now()
      // Only reset timeout if enough time has passed since last activity reset
      if (now - lastActivityRef.current > ACTIVITY_THROTTLE) {
        logger.debug('User activity detected - resetting session timeout')
        lastActivityRef.current = now
        resetSessionTimeout()
      }
    }
  }, [user, resetSessionTimeout])


  // Set up session timeout with activity detection
  useEffect(() => {
    if (user) {
      // Reset timeout when user becomes active
      resetSessionTimeout()

      // Add event listeners for user activity
      const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'wheel', 'click']
      
      const handleUserActivity = () => {
        checkUserActivity()
      }

      // Handle activity from other tabs
      const handleStorageChange = (e: StorageEvent) => {
        if (e.key === LAST_ACTIVITY_KEY && e.newValue) {
          const remoteLastActivity = parseInt(e.newValue, 10)
          if (remoteLastActivity > lastActivityRef.current) {
            lastActivityRef.current = remoteLastActivity
            resetSessionTimeout(true)
          }
        }
      }

      // Handle visibility changes (tab switching)
      const handleVisibilityChange = () => {
        if (document.hidden) {
          logger.debug('Tab hidden - pausing session timeout')
          if (sessionTimeoutRef.current) {
            clearTimeout(sessionTimeoutRef.current)
          }
        } else {
          logger.debug('Tab visible - resuming session timeout')
          // When becoming visible, check if we should reset based on other tabs' activity
          try {
            const lastRemote = localStorage.getItem(LAST_ACTIVITY_KEY)
            if (lastRemote) {
              const remoteLastActivity = parseInt(lastRemote, 10)
              if (remoteLastActivity > lastActivityRef.current) {
                lastActivityRef.current = remoteLastActivity
              }
            }
          } catch (e) {}
          resetSessionTimeout()
        }
      }

      // Add event listeners
      activityEvents.forEach(event => {
        document.addEventListener(event, handleUserActivity, { capture: true, passive: true })
      })
      document.addEventListener('visibilitychange', handleVisibilityChange)
      window.addEventListener('storage', handleStorageChange)

      return () => {
        if (sessionTimeoutRef.current) {
          clearTimeout(sessionTimeoutRef.current)
        }
        // Remove event listeners
        activityEvents.forEach(event => {
          document.removeEventListener(event, handleUserActivity, true)
        })
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        window.removeEventListener('storage', handleStorageChange)
      }
    }
  }, [user, resetSessionTimeout, checkUserActivity])

  useEffect(() => {
    let mounted = true

    // Add a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      if (mounted && isLoadingRef.current) {
        logger.warn('Auth timeout - setting loading to false')
        setIsLoading(false)
      }
    }, 10000) // 10 second timeout

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (mounted) {
        if (session?.user) {
          fetchUserProfile(session.user)
        } else {
          setIsLoading(false)
        }
      }
    }).catch((error) => {
      logger.error('Error getting session', error)
      if (mounted) {
        setIsLoading(false)
      }
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        logger.debug('Auth state changed', { event, userId: session?.user?.id })
        if (mounted) {
          // Only process auth changes if we don't already have a user or if it's a sign out
          if (session?.user) {
            // Check if we already have this user to prevent re-authentication
            if (currentUserIdRef.current === session.user.id) {
              logger.debug('User already authenticated, skipping re-fetch')
              return
            }
            currentUserIdRef.current = session.user.id
            await fetchUserProfile(session.user)
          } else if (!session?.user) {
            currentUserIdRef.current = null
            setUser(null)
            setIsLoading(false)
          }
        }
      }
    )

    return () => {
      mounted = false
      clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, [])

  const fetchUserProfile = async (authUser: User) => {
    try {
      logger.debug('Fetching profile for user', { userId: authUser.id })
      
      // Set user immediately to stop loading
      currentUserIdRef.current = authUser.id
      setUser({
        ...authUser,
        profile: undefined,
        memberships: []
      })
      
      logger.debug('User set immediately, now fetching profile')
      
          // Create minimal profile immediately to prevent blocking
          let finalProfile = {
            id: authUser.id,
            email: authUser.email || null,
            full_name: authUser.user_metadata?.full_name || authUser.email?.split('@')[0] || 'User',
            phone: authUser.user_metadata?.phone || null,
            whatsapp_notifications: true,
            status: 'active',
            avatar_url: authUser.user_metadata?.avatar_url || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }

          // Try to fetch real profile in background (non-blocking)
          const fetchProfileInBackground = async () => {
            try {
              logger.debug('Fetching profile in background')
              
              const profilePromise = supabase
                .from('profiles')
                .select('*')
                .eq('id', authUser.id)
                .single()

              const profileTimeout = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Profile fetch timeout')), 3000) // Reduced to 3 seconds
              )

              const { data: profile, error: profileError } = await Promise.race([
                profilePromise,
                profileTimeout
              ]) as { data: any, error: any }

              logger.debug('Profile fetch result', { hasProfile: !!profile, error: profileError?.code })

              if (profileError && profileError.code === 'PGRST116') {
                logger.debug('Profile not found, creating one')
                const { data: newProfile, error: createError } = await supabase
                  .from('profiles')
                  .insert({
                    id: authUser.id,
                    full_name: authUser.user_metadata?.full_name || '',
                    email: authUser.email || '',
                    phone: authUser.user_metadata?.phone || null
                  })
                  .select()
                  .single()

                if (createError) {
                  logger.error('Error creating profile', createError)
                } else {
                  logger.debug('Profile created successfully', { userId: newProfile?.id })
                  // Update user with real profile
                  setUser(prev => prev ? ({
                    ...prev,
                    profile: newProfile
                  }) : null)
                  
                  // After creating profile, auto-accept any pending invitations
                  try {
                    logger.debug('Auto-accepting invitations', { email: authUser.email })
                    const { data: invitations, error: inviteError } = await supabase.rpc('auto_accept_invitations_for_user', {
                      p_user_id: authUser.id,
                      p_user_email: authUser.email || ''
                    })
                    
                    if (inviteError) {
                      logger.debug('No invitations to auto-accept', { error: inviteError?.code })
                    } else {
                      logger.debug('Auto-accepted invitations', { count: invitations?.length || 0 })
                    }
                  } catch (inviteError) {
                    logger.warn('Error auto-accepting invitations', inviteError)
                  }
                }
              } else if (profileError) {
                logger.warn('Profile fetch error', profileError)
              } else {
                logger.debug('Profile found', { userId: profile?.id })
                // Update user with real profile
                setUser(prev => prev ? ({
                  ...prev,
                  profile: profile
                }) : null)
                
                // If profile exists, also try to auto-accept invitations (in case they were missed)
                try {
                  logger.debug('Checking for pending invitations for existing user', { email: authUser.email })
                  const { data: invitations, error: inviteError } = await supabase.rpc('auto_accept_invitations_for_user', {
                    p_user_id: authUser.id,
                    p_user_email: authUser.email || ''
                  })
                  
                  if (inviteError) {
                    logger.debug('No invitations to auto-accept', { error: inviteError?.code })
                  } else {
                    logger.debug('Auto-accepted invitations', { count: invitations?.length || 0 })
                  }
                } catch (inviteError) {
                  logger.warn('Error auto-accepting invitations', inviteError)
                }
              }
              
            } catch (profileError) {
              logger.warn('Background profile fetch failed', profileError)
              // Don't block the user experience - they already have a minimal profile
            }
          }

          // Update user with minimal profile immediately
          setUser(prev => prev ? ({
            ...prev,
            profile: finalProfile
          }) : null)

          // Start background profile fetch (non-blocking)
          fetchProfileInBackground()

          // Fetch user memberships in background (non-blocking)
          const fetchMembershipsInBackground = async () => {
            try {
              logger.debug('Fetching memberships in background')
              
              const membershipPromise = supabase
                .from('memberships')
                .select(`
                  *,
                  organizations (
                    name
                  )
                `)
                .eq('user_id', authUser.id)

              const membershipTimeout = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Membership fetch timeout')), 3000)
              )

              const { data: memberships, error: membershipError } = await Promise.race([
                membershipPromise,
                membershipTimeout
              ]) as { data: any, error: any }
              if (membershipError) {
                logger.warn('Membership fetch error', membershipError)
              } else {
                logger.debug('Memberships fetched', { count: memberships?.length || 0 })
                const membershipData = (memberships || []).map((m: any) => ({
                  ...m,
                  org_name: m.organizations?.name || 'Assigned Hotel'
                }))
                
                // Update user with memberships
                setUser(prev => prev ? ({
                  ...prev,
                  memberships: membershipData
                }) : null)
              }
              
            } catch (membershipError) {
              logger.warn('Background membership fetch failed', membershipError)
              // Don't block the user experience - they can continue without memberships
            }
          }

          // Start background membership fetch (non-blocking)
          fetchMembershipsInBackground()
      
      logger.debug('Profile fetch completed')
    } catch (error) {
      logger.error('Error in fetchUserProfile', error)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    setIsLoading(true)
    try {
      logger.debug('Attempting login', { email })
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (error) {
        logger.error('Login error', error)
        throw error
      }
      
      logger.debug('Login successful', { userId: data.user?.id })
    } catch (error) {
      logger.error('Login failed', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (name: string, email: string, password: string, phone: string) => {
    setIsLoading(true)
    try {
      logger.debug('Attempting registration', { email })
      // Set redirect URL for email verification to production URL with confirmation route
      const emailRedirectTo = 'https://productivityatlakecrest.com/confirm-email'
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo,
          data: {
            full_name: name,
            phone: phone
          }
        }
      })
      
      if (error) {
        logger.error('Registration error', error)
        throw error
      }
      
      logger.debug('Registration successful', { userId: data.user?.id })
      
      // If user was created successfully, fetch their profile
      if (data.user) {
        await fetchUserProfile(data.user)
        
        // Send email confirmation via Edge Function (replaces Supabase default email)
        try {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
          if (!supabaseUrl) {
            throw new Error('VITE_SUPABASE_URL is not configured')
          }
          const confirmationFunctionUrl = `${supabaseUrl}/functions/v1/send-email-confirmation`
          
          await fetch(confirmationFunctionUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({
              email: email,
              userName: name
            })
          })
        } catch (emailError) {
          // Log but don't fail registration if email fails
          logger.error('Failed to send email confirmation', emailError)
        }
        
        // Send welcome email via Edge Function
        try {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
          if (!supabaseUrl) {
            throw new Error('VITE_SUPABASE_URL is not configured')
          }
          const edgeFunctionUrl = `${supabaseUrl}/functions/v1/send-welcome-email`
          
          // Get user's hotel memberships for the welcome email
          const { data: membershipsData } = await supabase
            .from('memberships')
            .select(`
              role,
              organizations (
                name
              )
            `)
            .eq('user_id', data.user.id)
          
          const hotels = membershipsData?.map(m => ({
            name: m.organizations?.name || 'Unknown Hotel',
            role: m.role || 'user'
          })) || []
          
          await fetch(edgeFunctionUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({
              email: email,
              userName: name,
              hotels: hotels
            })
          })
        } catch (emailError) {
          // Log but don't fail registration if email fails
          logger.error('Failed to send welcome email', emailError)
        }
      }
    } catch (error) {
      logger.error('Registration failed', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user?.id) return

    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single()

      if (error) throw error

      setUser(prev => prev ? ({
        ...prev,
        profile: data
      }) : null)

      logger.debug('Profile updated successfully', { userId: user.id })
    } catch (error) {
      logger.error('Error updating profile', error)
      throw error
    }
  }

  const logout = async () => {
    setIsLoading(true)
    let shouldClearLocalState = false
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        throw error
      }
      shouldClearLocalState = true
    } catch (error) {
      if (!isMissingAuthSessionError(error)) {
        throw error
      }
      shouldClearLocalState = true
      logger.warn('Sign out requested with no active auth session; clearing local state')
    } finally {
      if (shouldClearLocalState) {
        try {
          localStorage.removeItem('selectedHotelId')
        } catch {}
        if (sessionTimeoutRef.current) {
          clearTimeout(sessionTimeoutRef.current)
          sessionTimeoutRef.current = null
        }
        currentUserIdRef.current = null
        setUser(null)
      }
      setIsLoading(false)
    }
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, register, updateProfile, isLoading, resetSessionTimeout }}>
      {children}
    </AuthContext.Provider>
  )
}
